import { useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, X } from 'lucide-react';

// Ícone de necessidades especiais
const SPECIAL_NEEDS_ICON = 'https://customer-assets.emergentagent.com/job_3e80ee87-15bc-48ab-81f1-95e18023f62e/artifacts/dw10yeqg_tdah.avif';

const PrintLabels = ({ checkins, eventName, eventDate, logo, onClose, autoPrint = false }) => {
  const printRef = useRef();
  const [isPrinting, setIsPrinting] = useState(false);

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: '--/--/----', time: '--:--' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const formatSequenceNumber = (num) => {
    return String(num || 0).padStart(3, '0');
  };

  // Verifica se é aniversariante (6 dias antes ou depois do evento)
  const isBirthday = (birthDate, eventDateStr) => {
    if (!birthDate || !eventDateStr) return false;
    try {
      // Extrair apenas a data (YYYY-MM-DD), ignorando a hora
      const eventDateOnly = eventDateStr.split('T')[0];
      const birthDateOnly = birthDate.split('T')[0];
      
      const eventD = new Date(eventDateOnly + 'T00:00:00');
      const birth = new Date(birthDateOnly + 'T00:00:00');
      
      // Criar data de aniversário no ano do evento
      const birthdayThisYear = new Date(eventD.getFullYear(), birth.getMonth(), birth.getDate());
      
      // Também verificar ano anterior e próximo (para casos de virada de ano)
      const birthdayLastYear = new Date(eventD.getFullYear() - 1, birth.getMonth(), birth.getDate());
      const birthdayNextYear = new Date(eventD.getFullYear() + 1, birth.getMonth(), birth.getDate());
      
      // Diferença em dias (considera os 3 cenários)
      const msPerDay = 1000 * 60 * 60 * 24;
      const diffThisYear = Math.abs(eventD - birthdayThisYear) / msPerDay;
      const diffLastYear = Math.abs(eventD - birthdayLastYear) / msPerDay;
      const diffNextYear = Math.abs(eventD - birthdayNextYear) / msPerDay;
      
      const minDiff = Math.min(diffThisYear, diffLastYear, diffNextYear);
      
      return minDiff <= 6;
    } catch {
      return false;
    }
  };

  // Verifica se é primeira vez
  const isFirstTime = (status) => {
    return status === 'visitante_primeira_vez';
  };

  // Verifica se tem necessidades especiais
  const hasSpecialNeeds = (specialNeeds) => {
    return specialNeeds === true;
  };

  const handlePrint = useCallback(() => {
    setIsPrinting(true);
    
    // Gerar HTML das etiquetas (2 por checkin: uma para pessoa, uma para responsável)
    const labelsHTML = checkins.flatMap((checkin, index) => {
      const { date, time } = formatDateTime(checkin.checkin_time || eventDate);
      const seqNum = formatSequenceNumber(checkin.sequence_number || index + 1);
      const guardianName = checkin.guardian_name || 'Responsável';
      const birthDate = checkin.birth_date || '';
      const adolStatus = checkin.adolescent_status || checkin.status || '';
      const specialNeeds = checkin.has_special_needs || false;
      
      const showBirthday = isBirthday(birthDate, eventDate);
      const showFirstTime = isFirstTime(adolStatus);
      const showSpecialNeeds = hasSpecialNeeds(specialNeeds);
      
      // Ícones
      const icons = [];
      if (showBirthday) icons.push('<span class="icon" title="Aniversariante">🎂</span>');
      if (showFirstTime) icons.push('<span class="icon" title="Primeira vez">⭐</span>');
      if (showSpecialNeeds) icons.push('<img class="icon-img" src="' + SPECIAL_NEEDS_ICON + '" alt="Necessidade Especial" title="' + specialNeeds + '" />');
      const iconsHTML = icons.length > 0 ? `<div class="icons-row">${icons.join('')}</div>` : '';
      
      // Etiqueta da PESSOA (Check-in)
      const personLabel = `
        <div class="label-container">
          <div class="label-left">
            <div class="label-number">
              <span class="number-value">${seqNum}</span>
            </div>
            ${iconsHTML}
          </div>
          
          <div class="label-info">
            <div class="label-name">${checkin.adolescent_name || 'Nome'}</div>
            <div class="label-guardian">Resp: ${guardianName}</div>
            <div class="label-event">${eventName || 'Evento'}</div>
            <div class="label-datetime">${date} • ${time}</div>
          </div>
          
          <div class="label-qr">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${seqNum}" alt="QR" />
          </div>
        </div>
      `;
      
      // Etiqueta do RESPONSÁVEL (Check-out)
      const guardianLabel = `
        <div class="label-container label-checkout">
          <div class="label-left">
            <div class="label-number checkout-number">
              <span class="number-value">${seqNum}</span>
              <span class="checkout-text">SAÍDA</span>
            </div>
            ${iconsHTML}
          </div>
          
          <div class="label-info">
            <div class="label-name">${checkin.adolescent_name || 'Nome'}</div>
            <div class="label-guardian">Resp: ${guardianName}</div>
            <div class="label-event">${eventName || 'Evento'}</div>
            <div class="label-datetime">${date} • ${time}</div>
          </div>
          
          <div class="label-qr">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${seqNum}" alt="QR" />
          </div>
        </div>
      `;
      
      return [personLabel, guardianLabel];
    }).join('');
    
    // Abrir nova janela para impressão
    const printWindow = window.open('', '_blank', 'width=500,height=700');
    
    if (!printWindow) {
      alert('Por favor, permita pop-ups para imprimir as etiquetas.');
      setIsPrinting(false);
      return;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Etiquetas de Check-in</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              background: white;
              padding: 0;
              margin: 0;
            }
            @media print {
              @page { 
                size: 90mm 28mm; 
                margin: 0; 
              }
              body { 
                margin: 0; 
                padding: 0; 
              }
              .label-container { 
                page-break-after: always; 
                page-break-inside: avoid; 
                margin: 0 !important;
                border: none !important;
                border-radius: 0 !important;
              }
              .label-container:last-child { 
                page-break-after: auto; 
              }
              .no-print {
                display: none !important;
              }
            }
            .print-header {
              padding: 20px;
              text-align: center;
              border-bottom: 1px solid #ddd;
              margin-bottom: 20px;
              background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
            }
            .print-header h1 {
              font-size: 18px;
              color: #0369a1;
              margin-bottom: 8px;
            }
            .print-header p {
              font-size: 14px;
              color: #666;
              margin-bottom: 5px;
            }
            .print-btn {
              background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
              color: white;
              border: none;
              padding: 14px 40px;
              font-size: 16px;
              font-weight: bold;
              border-radius: 8px;
              cursor: pointer;
              margin-top: 15px;
              display: inline-flex;
              align-items: center;
              gap: 8px;
            }
            .print-btn:hover {
              opacity: 0.9;
              transform: translateY(-1px);
            }
            .labels-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 8px;
              padding: 20px;
            }
            .label-container {
              width: 340px;
              height: 106px;
              padding: 4px 6px;
              background-color: white;
              display: flex;
              align-items: center;
              gap: 6px;
              box-sizing: border-box;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
            }
            .label-checkout {
              border-left: 4px solid #f97316;
            }
            .label-left {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 2px;
              flex-shrink: 0;
            }
            .label-number {
              width: 50px;
              height: 50px;
              flex-shrink: 0;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
              border-radius: 8px;
            }
            .checkout-number {
              background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
              height: 50px;
            }
            .number-value {
              font-size: 22px;
              font-weight: bold;
              color: white;
              font-family: 'Arial Black', Arial, sans-serif;
              line-height: 1;
            }
            .checkout-text {
              font-size: 7px;
              color: white;
              font-weight: bold;
              margin-top: 1px;
              letter-spacing: 0.5px;
            }
            .icons-row {
              display: flex;
              gap: 2px;
              align-items: center;
              justify-content: center;
            }
            .icon {
              font-size: 14px;
            }
            .icon-img {
              width: 16px;
              height: 16px;
              object-fit: contain;
            }
            .label-info {
              flex: 1;
              overflow: hidden;
              min-width: 0;
              display: flex;
              flex-direction: column;
              justify-content: center;
            }
            .label-name {
              font-size: 18px;
              font-weight: bold;
              color: #1e293b;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              line-height: 1.1;
            }
            .label-guardian {
              font-size: 10px;
              color: #475569;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              line-height: 1.2;
            }
            .label-event {
              font-size: 9px;
              color: #64748b;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              line-height: 1.2;
            }
            .label-datetime {
              font-size: 9px;
              color: #64748b;
              white-space: nowrap;
              line-height: 1.2;
            }
            .label-qr {
              flex-shrink: 0;
              width: 60px;
              height: 60px;
            }
            .label-qr img { 
              width: 60px; 
              height: 60px; 
            }
            .info-text {
              font-size: 12px;
              color: #666;
              margin-top: 10px;
            }
            .legend {
              display: flex;
              gap: 15px;
              justify-content: center;
              margin-top: 10px;
              font-size: 12px;
              color: #666;
            }
            .legend span {
              display: flex;
              align-items: center;
              gap: 4px;
            }
            .legend img {
              width: 16px;
              height: 16px;
            }
          </style>
        </head>
        <body>
          <div class="print-header no-print">
            <h1>🏷️ Etiquetas de Check-in</h1>
            <p><strong>${checkins.length * 2}</strong> etiquetas (${checkins.length} check-ins + ${checkins.length} saídas)</p>
            <p>${eventName || 'Evento'}</p>
            <div class="legend">
              <span>🎂 Aniversariante</span>
              <span>⭐ Primeira vez</span>
              <span><img src="${SPECIAL_NEEDS_ICON}" alt="" /> Necessidade especial</span>
            </div>
            <button class="print-btn" onclick="window.print()">
              🖨️ Imprimir Etiquetas
            </button>
            <p class="info-text">Configure a impressora para papel 90mm x 28mm</p>
          </div>
          
          <div class="labels-container">
            ${labelsHTML}
          </div>
          
          <script>
            window.onload = function() {
              window.focus();
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setIsPrinting(false);
    
    if (autoPrint) {
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  }, [checkins, eventName, eventDate, autoPrint, onClose]);

  useEffect(() => {
    if (autoPrint && checkins.length > 0) {
      const timer = setTimeout(() => {
        handlePrint();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoPrint, checkins.length, handlePrint]);

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Imprimir Etiquetas</h2>
            <p className="text-sm text-gray-500">{checkins.length * 2} etiquetas ({checkins.length} check-ins + {checkins.length} saídas) • 90mm x 28mm</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Legenda */}
        <div className="px-4 py-2 bg-gray-50 border-b flex gap-4 text-sm text-gray-600 items-center">
          <span>🎂 Aniversariante</span>
          <span>⭐ Primeira vez</span>
          <span className="flex items-center gap-1">
            <img src={SPECIAL_NEEDS_ICON} alt="" className="w-4 h-4" /> Necessidade especial
          </span>
        </div>
        
        {/* Preview */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100">
          <div ref={printRef} className="flex flex-col items-center gap-3">
            {checkins.flatMap((checkin, index) => {
              const { date, time } = formatDateTime(checkin.checkin_time || eventDate);
              const seqNum = formatSequenceNumber(checkin.sequence_number || index + 1);
              const guardianName = checkin.guardian_name || 'Responsável';
              const birthDate = checkin.birth_date || '';
              const adolStatus = checkin.adolescent_status || checkin.status || '';
              const specialNeeds = checkin.has_special_needs || false;
              
              const showBirthday = isBirthday(birthDate, eventDate);
              const showFirstTime = isFirstTime(adolStatus);
              const showSpecialNeeds = hasSpecialNeeds(specialNeeds);
              
              return [
                // Etiqueta da PESSOA
                <div 
                  key={`person-${checkin.id || index}`} 
                  className="bg-white rounded-lg shadow-md flex items-center gap-1.5"
                  style={{ width: '340px', height: '106px', padding: '4px 6px' }}
                >
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className="w-[50px] h-[50px] flex flex-col items-center justify-center bg-gradient-to-br from-sky-500 to-sky-600 rounded-lg">
                      <span className="text-[22px] font-black text-white leading-none">{seqNum}</span>
                    </div>
                    <div className="flex gap-1 items-center">
                      {showBirthday && <span title="Aniversariante" className="text-sm">🎂</span>}
                      {showFirstTime && <span title="Primeira vez" className="text-sm">⭐</span>}
                      {showSpecialNeeds && <img src={SPECIAL_NEEDS_ICON} alt="Necessidade Especial" title="Necessidade Especial" className="w-4 h-4" />}
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-hidden min-w-0 flex flex-col justify-center">
                    <div className="text-[18px] font-bold text-gray-900 truncate leading-tight">
                      {checkin.adolescent_name || 'Nome'}
                    </div>
                    <div className="text-[10px] text-gray-600 truncate leading-tight">
                      Resp: {guardianName}
                    </div>
                    <div className="text-[9px] text-gray-500 truncate leading-tight">
                      {eventName || 'Evento'}
                    </div>
                    <div className="text-[9px] text-gray-500 leading-tight">
                      {date} • {time}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <QRCodeSVG 
                      value={seqNum}
                      size={60}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                </div>,
                
                // Etiqueta do RESPONSÁVEL (saída)
                <div 
                  key={`guardian-${checkin.id || index}`} 
                  className="bg-white rounded-lg shadow-md flex items-center gap-1.5 border-l-4 border-orange-500"
                  style={{ width: '340px', height: '106px', padding: '4px 6px' }}
                >
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className="w-[50px] h-[50px] flex flex-col items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg">
                      <span className="text-[22px] font-black text-white leading-none">{seqNum}</span>
                      <span className="text-[7px] text-white font-bold mt-0.5 tracking-wide">SAÍDA</span>
                    </div>
                    <div className="flex gap-1 items-center">
                      {showBirthday && <span title="Aniversariante" className="text-sm">🎂</span>}
                      {showFirstTime && <span title="Primeira vez" className="text-sm">⭐</span>}
                      {showSpecialNeeds && <img src={SPECIAL_NEEDS_ICON} alt="Necessidade Especial" title="Necessidade Especial" className="w-4 h-4" />}
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-hidden min-w-0 flex flex-col justify-center">
                    <div className="text-[18px] font-bold text-gray-900 truncate leading-tight">
                      {checkin.adolescent_name || 'Nome'}
                    </div>
                    <div className="text-[10px] text-gray-600 truncate leading-tight">
                      Resp: {guardianName}
                    </div>
                    <div className="text-[9px] text-gray-500 truncate leading-tight">
                      {eventName || 'Evento'}
                    </div>
                    <div className="text-[9px] text-gray-500 leading-tight">
                      {date} • {time}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <QRCodeSVG 
                      value={seqNum}
                      size={60}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                </div>
              ];
            })}
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            Fechar
          </button>
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className="px-6 py-2 bg-gradient-to-r from-sky-400 to-sky-500 text-white rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2 disabled:opacity-50"
          >
            <Printer size={18} />
            {isPrinting ? 'Abrindo...' : 'Imprimir'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrintLabels;
